/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box } from '@chakra-ui/react';
import resolve from 'lib/resolve';
import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { TableDefinition } from './DataTableModels';

export default function TableHits({
  hits,
  tableDef,
}: {
  hits: Array<any>;
  tableDef: TableDefinition;
}): JSX.Element {
  const [sort, setSort] = useState<{
    accessor: string;
    direction: 'asc' | 'desc';
  } | null>(null);

  const sortedHits = useMemo(() => {
    if (!sort) return hits;

    return [...hits].sort((left, right) => {
      const leftValue = resolve(left, sort.accessor);
      const rightValue = resolve(right, sort.accessor);

      if (leftValue === rightValue) return 0;
      if (leftValue == null) return 1;
      if (rightValue == null) return -1;

      const comparison =
        typeof leftValue === 'boolean' && typeof rightValue === 'boolean'
          ? Number(leftValue) - Number(rightValue)
          : String(leftValue).localeCompare(String(rightValue), 'sv-SE', {
              numeric: true,
              sensitivity: 'base',
            });

      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [hits, sort]);

  function handleSort(accessor: string): void {
    setSort((currentSort) => ({
      accessor,
      direction:
        currentSort?.accessor === accessor && currentSort.direction === 'asc'
          ? 'desc'
          : 'asc',
    }));
  }

  function getDataPoint(hit: any, column: any): JSX.Element {
    if (tableDef.noLinks) {
      return <span>{resolve(hit, column.accessor)}</span>;
    }

    return (
      <Link
        to={`${tableDef.overrideViewRowUrl || window.location.pathname}/${
          hit.objectID ?? hit.id
        }`}>
        {resolve(hit, column.accessor)}
      </Link>
    );
  }

  return (
    <Box>
      <table className="data-table">
        <thead>
          <tr>
            {tableDef.columns.map((column) => (
              <th
                key={column.header}
                onClick={
                  column.sortable
                    ? (): void => handleSort(column.accessor)
                    : undefined
                }
                style={column.sortable ? { cursor: 'pointer' } : undefined}>
                {column.header}
                {sort?.accessor === column.accessor &&
                  (sort.direction === 'asc' ? ' ↑' : ' ↓')}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedHits.map((hit, i) => {
            return (
              <tr
                key={hit.objectID || hit.id || i}
                style={{
                  ...(tableDef.applyRowStyle
                    ? tableDef.applyRowStyle(hit)
                    : undefined),
                  ...tableDef.rowStyles,
                }}>
                {tableDef.columns.map((column) => (
                  <td key={column.accessor} style={column.tdStyles}>
                    {column.customHtml
                      ? column.customHtml(hit)
                      : getDataPoint(hit, column)}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </Box>
  );
}
